import Properties from '@/components/properties';
import { Card, CardHeader, CardTitle } from '@/components/ui/largeCard';
import { OccurrenceMediaDetailsFragment, OccurrenceQuery, Term } from '@/gql/graphql';
import { truncateMiddle } from '@/utils/truncateString';
import { useEffect, useState } from 'react';
import { MdFileDownload } from 'react-icons/md';
import { RiExternalLinkLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';
import { BasicField, licenseMap } from '../properties';
import { Img } from '@/components/Img';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DynamicLink } from '@/reactRouterPlugins';
import getTitleParts from '../getTitle';

const supportedFormats = [
  'audio/ogg',
  'audio/x-wav',
  'audio/mpeg',
  'audio/wav',
  'audio/mp3',
  'audio/mp4',
] as const;

export function Media({
  occurrence,
  termMap,
  loading = false,
  updateToc = () => {},
}: {
  occurrence: OccurrenceQuery['occurrence'];
  termMap: { [key: string]: Term };
  loading?: boolean;
  error?: any;
  updateToc: (id: string, visible: boolean) => void;
  className?: string;
}) {
  const sectionName = 'multimedia';
  const [visible, setVisible] = useState<boolean | undefined>();
  useEffect(() => {
    if (typeof visible === 'boolean') updateToc(sectionName, visible);
  }, [visible]);

  if (loading || !occurrence) return <h2>Loading</h2>; //TODO replace with proper skeleton loader
  if (
    occurrence.stillImageCount === 0 &&
    occurrence.soundCount === 0 &&
    occurrence.movingImageCount === 0
  ) {
    if (visible) setVisible(false);
    return null;
  } else {
    if (!visible) setVisible(true);
  }

  return (
    <div className="g-mb-4 g-scroll-mt-24" id="multimedia">
      <CardHeader>
        <CardTitle>
          <FormattedMessage id="occurrenceDetails.extensions.multimedia.name" />
        </CardTitle>
      </CardHeader>
      <ul className="g-grid g-grid-cols-1 md:g-grid-cols-2 g-gap-4">
        <Sounds {...{ occurrence, termMap }} />
        <MovingImages {...{ occurrence, termMap }} />
        <Images occurrence={occurrence} termMap={termMap} />
      </ul>
    </div>
  );
}

function Images({
  occurrence,
  termMap,
}: {
  occurrence: OccurrenceQuery['occurrence'];
  termMap: { [key: string]: Term };
}) {
  if (!occurrence) return null;
  const imageCount = occurrence.stillImageCount || 0;
  const cap = 100;
  return (
    <>
      {imageCount > cap && (
        <Alert variant="theme" className="g-mb-4">
          <AlertDescription>
            Showing first 100 images out of {imageCount}.<br />
            Please refer to the API or do a download for the complete list.
          </AlertDescription>
        </Alert>
      )}
      {occurrence.stillImages?.slice(0, cap).map((media: OccurrenceMediaDetailsFragment, index) => (
        <li key={`${media?.identifier}_${index}`}>
          <Card className="g-overflow-hidden">
            {media.thumbor && (
              <figure>
                <a
                  target="_blank"
                  href={`${import.meta.env.PUBLIC_BASE_URL}/tools/zoom/simple.html?src=${encodeURIComponent(
                    media.originalImage ?? media.thumbor
                  )}`}
                >
                  <Img src={media.thumbor} failedClassName="g-h-36 g-bg-slate-200" />
                </a>
              </figure>
            )}
            <Caption media={media} occurrence={occurrence} termMap={termMap} />
          </Card>
        </li>
      ))}
    </>
  );
}

function Sounds({
  occurrence,
  termMap,
}: {
  occurrence: OccurrenceQuery['occurrence'];
  termMap: { [key: string]: Term };
}) {
  if (!occurrence) return null;
  return (
    <>
      {occurrence.sounds?.map((media: OccurrenceMediaDetailsFragment, index) => {
        const format = media.format;
        const knownFormat = format && (supportedFormats as readonly string[]).includes(format);
        return (
          <li key={`${media?.identifier}_${index}`}>
            <Card className="g-overflow-hidden">
              <div>
                {knownFormat && media.identifier && (
                  <>
                    <div className="g-flex g-justify-center g-p-4">
                      <audio controls>
                        <source src={media.identifier} type={format} />
                        Unable to play
                      </audio>
                    </div>
                    {(termMap?.references?.value || media.identifier) && (
                      <TryPublisherSiteLink
                        href={termMap?.references?.value || media.identifier!}
                      />
                    )}
                  </>
                )}
              </div>
              <Caption media={media} occurrence={occurrence} termMap={termMap} />
            </Card>
          </li>
        );
      })}
    </>
  );
}

function MovingImages({
  occurrence,
  termMap,
}: {
  occurrence: OccurrenceQuery['occurrence'];
  termMap: { [key: string]: Term };
}) {
  if (!occurrence) return null;
  return (
    <>
      {occurrence.movingImages?.map((media: OccurrenceMediaDetailsFragment, index) => {
        if (!media) return null;
        const format = media.format;
        const knownFormat = format && ['video/mp4', 'video/ogg'].includes(format);
        return (
          <li key={`${media?.identifier}_${index}`}>
            <Card className="g-overflow-hidden">
              <div>
                {knownFormat && media.identifier && (
                  <>
                    <video controls className="g-w-full">
                      <source src={media.identifier} type={format} />
                      Unable to play
                    </video>
                    {(termMap?.references?.value || media.identifier) && (
                      <TryPublisherSiteLink
                        href={termMap?.references?.value || media.identifier!}
                      />
                    )}
                  </>
                )}
                {!knownFormat && media.identifier && (
                  <a href={media.identifier}>
                    <div className="gb-download-icon">
                      <MdFileDownload />
                    </div>
                    <div>
                      <FormattedMessage id="occurrenceDetails.downloadMediaFile" />
                    </div>
                  </a>
                )}
              </div>
              <Caption media={media} occurrence={occurrence} termMap={termMap} />
            </Card>
          </li>
        );
      })}
    </>
  );
}

function TryPublisherSiteLink({ href }: { href: string }) {
  return (
    <div>
      <a href={href} className="g-px-2 g-py-1 g-bg-slate-300 g-text-slate-600 g-text-sm g-block">
        <FormattedMessage id="occurrenceDetails.tryPublisherSiteForFailingMedia" />{' '}
        <RiExternalLinkLine className="g-inline" />
      </a>
    </div>
  );
}

function Caption({
  media,
  occurrence,
  termMap,
}: {
  media: OccurrenceMediaDetailsFragment;
  occurrence: OccurrenceQuery['occurrence'];
  termMap: { [key: string]: Term };
}) {
  return (
    <figcaption className="g-px-4 g-py-2 [&_a]:g-underline">
      {!media.identifier && (
        <div className="g-bg-slate-200 g-rounded g-text-slate-800 g-px-2 g-py-1 g-mb-2">
          Identifier missing
        </div>
      )}
      <Properties style={{ fontSize: '85%' }} dense>
        {media.description && (
          <BasicField label={`occurrenceFieldNames.description`}>{media.description}</BasicField>
        )}
        {media.format && (
          <BasicField label={`occurrenceFieldNames.format`}>{media.format}</BasicField>
        )}
        {media.identifier && (
          <BasicField label={`occurrenceFieldNames.identifier`}>
            <a href={media.identifier} className="g-text-inherit">
              {truncateMiddle(media.identifier, 40)}
            </a>
          </BasicField>
        )}
        {media.references && (
          <BasicField label={`occurrenceFieldNames.references`}>
            <a href={media.references} className="g-text-inherit">
              {truncateMiddle(media.references, 40)}
            </a>
          </BasicField>
        )}
        {media.creator && (
          <BasicField label={`occurrenceFieldNames.creator`}>{media.creator}</BasicField>
        )}
        {media.publisher && (
          <BasicField label={`occurrenceFieldNames.publisher`}>{media.publisher}</BasicField>
        )}
        {media.rightsHolder && (
          <BasicField label={`occurrenceFieldNames.rightsHolder`}>{media.rightsHolder}</BasicField>
        )}
        {media.license && (
          <BasicField label={`occurrenceFieldNames.license`}>
            <LicenseValue license={media.license} />
          </BasicField>
        )}
        {media.created && (
          <BasicField label={`occurrenceFieldNames.created`}>{media.created}</BasicField>
        )}
        {isValidCreativeCommonsLicense(media.license) && occurrence?.key && (
          <BasicField label={`occurrenceDetails.suggestedCitation`}>
            <SuggestedAttribution media={media} occurrence={occurrence} termMap={termMap} />
          </BasicField>
        )}
      </Properties>
    </figcaption>
  );
}

export function isValidCreativeCommonsLicense(license?: string | null): boolean {
  if (!license) return false;
  if (licenseMap[license]) return true;
  const v = license.toLowerCase();
  if (v.includes('creativecommons') || v.includes('creative commons')) return true;
  if (v.startsWith('cc-') || v.startsWith('cc_')) return true;
  return false;
}

export function SuggestedAttribution({
  media,
  occurrence,
  termMap,
}: {
  media: OccurrenceMediaDetailsFragment;
  occurrence: NonNullable<OccurrenceQuery['occurrence']>;
  termMap: { [key: string]: Term };
}) {
  const { title, state } = getTitleParts({ occurrence, termMap });
  return (
    <span dir="auto">
      {media.title && <>&ldquo;{media.title}&rdquo; - </>}
      {title && (
        <DynamicLink
          pageId="occurrenceKey"
          variables={{ key: String(occurrence.key) }}
          className="g-underline g-text-inherit"
        >
          {/* only the matched name is formatted, the verbatim name is shown as text like in the page header */}
          {state === 'MATCH_NO_ISSUES' ? (
            <span dangerouslySetInnerHTML={{ __html: title }} />
          ) : (
            <span>{title}</span>
          )}
        </DynamicLink>
      )}
      {occurrence.countryCode && (
        <>
          {' '}
          <FormattedMessage
            id={`occurrenceDetails.inCountry.${occurrence.basisOfRecord ?? 'UNKNOWN'}`}
            defaultMessage="Recorded in {COUNTRY}"
            values={{
              COUNTRY: (
                <FormattedMessage
                  id={`enums.countryCode.${occurrence.countryCode}`}
                  defaultMessage={occurrence.countryCode}
                />
              ),
            }}
          />
        </>
      )}
      {media.rightsHolder && (
        <>
          {' '}
          <FormattedMessage
            id="occurrenceDetails.byRightsHolder"
            defaultMessage="by {RIGHTSHOLDER}"
            values={{ RIGHTSHOLDER: media.rightsHolder }}
          />
        </>
      )}
      {' ('}
      <FormattedMessage
        id="occurrenceDetails.licensedUnder"
        defaultMessage="licensed under {LICENSE}"
        values={{ LICENSE: <LicenseValue license={media.license!} /> }}
      />
      {')'}
    </span>
  );
}

function LicenseValue({ license }: { license: string }) {
  // Check if it's an enum key (e.g. CC_BY_4_0)
  const enumUrl = licenseMap[license];
  if (enumUrl)
    return (
      <a href={enumUrl} className="g-underline">
        {license}
      </a>
    );

  // Check if the value itself is a known license URL (http/https, with or without /legalcode)
  const normalize = (u: string) =>
    u
      .replace(/^https?:\/\//, '')
      .replace(/\/legalcode$/, '')
      .replace(/\/$/, '');
  const matchedEnumKey = Object.entries(licenseMap).find(
    ([, url]) => normalize(license) === normalize(url)
  )?.[0];
  if (matchedEnumKey)
    return (
      <a href={license} className="g-underline g-text-inherit">
        <FormattedMessage id={`enums.license.${matchedEnumKey}`} defaultMessage={matchedEnumKey} />
      </a>
    );

  return <>{license}</>;
}
